import React, { useState, useEffect, useCallback } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import { Badge } from './ui/badge';
import { Users, Globe, Eye, Mail, TrendingUp, MapPin, Monitor, Smartphone, Tablet } from 'lucide-react';
import { Tabs, TabsContent, TabsList, TabsTrigger } from './ui/tabs';
import QuotesTab from './QuotesTab';
import AdminHeader from './AdminHeader';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const ANALYTICS_REFRESH_INTERVAL_MS = 30000; // 30 seconds

// Helper function to format timestamps
const formatTimestamp = (timestamp) => {
  return new Date(timestamp).toLocaleString();
};

// Helper function to get flag emoji
const getFlagEmoji = (countryCode) => {
  if (!countryCode || countryCode === 'Unknown') return '🌍';
  return countryCode.toUpperCase().replace(/./g, char => 
    String.fromCodePoint(127397 + char.charCodeAt())
  );
};

// Helper function to get device icon
const getDeviceIcon = (type) => {
  const icons = {
    mobile: <Smartphone className="w-4 h-4" />,
    tablet: <Tablet className="w-4 h-4" />,
    desktop: <Monitor className="w-4 h-4" />
  };
  return icons[type] || icons.desktop;
};

const AdminDashboard = () => {
  const [analytics, setAnalytics] = useState(null);
  const [visits, setVisits] = useState([]);
  const [contacts, setContacts] = useState([]);
  const [loading, setLoading] = useState(true);

  const fetchAnalytics = useCallback(async () => {
    if (!BACKEND_URL) return;
    
    try {
      const [analyticsRes, visitsRes, contactsRes] = await Promise.all([
        axios.get(`${BACKEND_URL}/api/analytics/summary`),
        axios.get(`${BACKEND_URL}/api/analytics/visits?limit=20`),
        axios.get(`${BACKEND_URL}/api/analytics/contacts?limit=20`)
      ]);

      setAnalytics(analyticsRes.data);
      setVisits(visitsRes.data.visits);
      setContacts(contactsRes.data.contacts);
      setLoading(false);
    } catch (error) {
      // Log only in development
      if (process.env.NODE_ENV === 'development') {
        console.error('Failed to fetch analytics:', error);
      }
      setLoading(false);
    }
    // BACKEND_URL is from env and won't change, setters are stable
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    fetchAnalytics();
    const interval = setInterval(fetchAnalytics, ANALYTICS_REFRESH_INTERVAL_MS);
    return () => clearInterval(interval);
    // fetchAnalytics is memoized, ANALYTICS_REFRESH_INTERVAL_MS is constant
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fetchAnalytics]);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="text-center">
          <TrendingUp className="w-12 h-12 mx-auto mb-4 animate-pulse" style={{ color: '#D97904' }} />
          <p className="text-gray-600">Loading analytics...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 py-8 px-4 sm:px-6 lg:px-8">
      <div className="max-w-7xl mx-auto">
        <AdminHeader title="Analytics Dashboard" subtitle="iWurks360Digital Solutions - Website Analytics" />

        {/* Stats Overview */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="text-sm font-medium flex items-center">
                <Eye className="w-4 h-4 mr-2" style={{ color: '#D97904' }} />
                Total Visits
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="text-3xl font-bold text-gray-900">
                {analytics?.total_visits?.toLocaleString() || 0}
              </div>
              <p className="text-xs text-gray-600 mt-1">All time page views</p>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="text-sm font-medium flex items-center">
                <Users className="w-4 h-4 mr-2" style={{ color: '#D97904' }} />
                Unique Visitors
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="text-3xl font-bold text-gray-900">
                {analytics?.unique_visitors?.toLocaleString() || 0}
              </div>
              <p className="text-xs text-gray-600 mt-1">Distinct visitors</p>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="text-sm font-medium flex items-center">
                <Mail className="w-4 h-4 mr-2" style={{ color: '#D97904' }} />
                Contact Submissions
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="text-3xl font-bold text-gray-900">
                {contacts?.length || 0}
              </div>
              <p className="text-xs text-gray-600 mt-1">Recent inquiries</p>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="text-sm font-medium flex items-center">
                <Globe className="w-4 h-4 mr-2" style={{ color: '#D97904' }} />
                Countries
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="text-3xl font-bold text-gray-900">
                {analytics?.top_countries?.length || 0}
              </div>
              <p className="text-xs text-gray-600 mt-1">Unique locations</p>
            </CardContent>
          </Card>
        </div>

        {/* Tabs for different views */}
        <Tabs defaultValue="visitors" className="space-y-6">
          <TabsList className="grid w-full grid-cols-5 lg:w-auto">
            <TabsTrigger value="visitors">Visitors</TabsTrigger>
            <TabsTrigger value="locations">Locations</TabsTrigger>
            <TabsTrigger value="contacts">Contacts</TabsTrigger>
            <TabsTrigger value="quotes" data-testid="admin-tab-quotes">Quotes</TabsTrigger>
            <TabsTrigger value="devices">Devices</TabsTrigger>
          </TabsList>

          <TabsContent value="quotes">
            <QuotesTab />
          </TabsContent>

          {/* Recent Visitors Tab */}
          <TabsContent value="visitors">
            <Card>
              <CardHeader>
                <CardTitle>Recent Visitors</CardTitle>
                <CardDescription>Last 20 website visits</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  {visits.map((visit) => (
                    <div key={visit._id || visit.timestamp} className="flex items-center justify-between p-4 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors">
                      <div className="flex items-center space-x-4 flex-1">
                        <div className="w-10 h-10 rounded-full flex items-center justify-center" style={{ backgroundColor: '#FFF5E6' }}>
                          {getDeviceIcon(visit.device?.type)}
                        </div>
                        <div className="flex-1">
                          <div className="flex items-center space-x-2">
                            <MapPin className="w-4 h-4 text-gray-400" />
                            <span className="font-medium text-gray-900">
                              {visit.location?.city}, {visit.location?.country}
                            </span>
                          </div>
                          <div className="text-sm text-gray-600">
                            {visit.browser?.name} on {visit.os?.name} • {visit.path}
                          </div>
                        </div>
                      </div>
                      <div className="text-right">
                        <div className="text-sm text-gray-600">
                          {formatTimestamp(visit.timestamp).split(',')[0]}
                        </div>
                        <div className="text-xs text-gray-500">
                          {formatTimestamp(visit.timestamp).split(',')[1]}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Locations Tab */}
          <TabsContent value="locations">
            <div className="grid md:grid-cols-2 gap-6">
              <Card>
                <CardHeader>
                  <CardTitle>Top Countries</CardTitle>
                  <CardDescription>Visitor distribution by country</CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="space-y-3">
                    {analytics?.top_countries?.map((country) => (
                      <div key={country._id} className="flex items-center justify-between">
                        <div className="flex items-center space-x-2">
                          <span className="text-xl">{getFlagEmoji(country._id)}</span>
                          <span className="font-medium text-gray-900">{country._id}</span>
                        </div>
                        <Badge style={{ backgroundColor: '#D97904' }} className="text-white">
                          {country.count} visits
                        </Badge>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>

              <Card>
                <CardHeader>
                  <CardTitle>Top Pages</CardTitle>
                  <CardDescription>Most visited pages</CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="space-y-3">
                    {analytics?.top_pages?.map((page) => (
                      <div key={page._id} className="flex items-center justify-between">
                        <span className="font-medium text-gray-900 truncate">{page._id}</span>
                        <Badge variant="outline">{page.visits} views</Badge>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            </div>
          </TabsContent>

          {/* Contact Submissions Tab */}
          <TabsContent value="contacts">
            <Card>
              <CardHeader>
                <CardTitle>Contact Form Submissions</CardTitle>
                <CardDescription>Recent inquiries from potential clients</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  {contacts.map((contact) => (
                    <div key={contact._id || contact.id} className="p-4 border border-gray-200 rounded-lg hover:border-orange-300 transition-colors">
                      <div className="flex justify-between items-start mb-3">
                        <div>
                          <h4 className="font-semibold text-gray-900">{contact.name}</h4>
                          <p className="text-sm text-gray-600">{contact.company || 'No company'}</p>
                        </div>
                        <Badge style={{ backgroundColor: '#D97904' }} className="text-white">
                          {contact.service || 'General Inquiry'}
                        </Badge>
                      </div>
                      <div className="grid grid-cols-2 gap-4 mb-3">
                        <div>
                          <p className="text-xs text-gray-500">Email</p>
                          <a href={`mailto:${contact.email}`} className="text-sm text-blue-600 hover:underline">
                            {contact.email}
                          </a>
                        </div>
                        <div>
                          <p className="text-xs text-gray-500">Phone</p>
                          <p className="text-sm text-gray-900">{contact.phone || 'Not provided'}</p>
                        </div>
                      </div>
                      <div className="mb-2">
                        <p className="text-xs text-gray-500">Budget Range</p>
                        <p className="text-sm text-gray-900">{contact.budget || 'Not specified'}</p>
                      </div>
                      <div className="bg-gray-50 p-3 rounded">
                        <p className="text-xs text-gray-500 mb-1">Message</p>
                        <p className="text-sm text-gray-700">{contact.message}</p>
                      </div>
                      <div className="mt-3 text-xs text-gray-500">
                        Submitted: {new Date(contact.submitted_at).toLocaleString()}
                      </div>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Devices Tab */}
          <TabsContent value="devices">
            <Card>
              <CardHeader>
                <CardTitle>Device Breakdown</CardTitle>
                <CardDescription>Visitor devices and browsers</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="grid md:grid-cols-3 gap-4">
                  {analytics?.device_breakdown?.map((device) => (
                    <div key={device._id} className="text-center p-6 bg-gray-50 rounded-lg">
                      <div className="w-16 h-16 mx-auto mb-3 rounded-full flex items-center justify-center" style={{ backgroundColor: '#FFF5E6' }}>
                        {getDeviceIcon(device._id)}
                      </div>
                      <h4 className="font-semibold text-gray-900 capitalize mb-1">{device._id}</h4>
                      <p className="text-2xl font-bold" style={{ color: '#D97904' }}>{device.count}</p>
                      <p className="text-sm text-gray-600">visits</p>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
};

export default AdminDashboard;
